import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from '../useLatest'
import { Users, GitCompare } from 'lucide-react'
import { useBruktbilFilters } from '../useBruktbilFilters'
import { useExplanationFilterLines } from '../explanationFilters'
import { fetchSelgere } from './api'
import type { SelgereResponse } from './api'
import SelgereTable from './SelgereTable'
import OrgSammenligningTab from '../OrgSammenligningTab'
import SubTabBar from '../SubTabBar'
import ClassificationFilter from '../ClassificationFilter'
import FilterSlot, { useLocalFilter } from '../FilterSlot'
import QueryExplanationBox from '../../../components/QueryExplanationBox'
import { useMaxDataDate } from '../../../hooks/useMaxDataDate'

// Second-level tabs under Bruktbil → Selgere, same mechanism as Ståtid's:
// the active tab lives in the page-local `tab` URL param.
const SUB_TABS = [
  { key: 'topp', labelKey: 'orgList.toppSelgere', icon: Users },
  { key: 'sammenligning', labelKey: 'merkeModell.tabs.sammenligning', icon: GitCompare },
] as const

type SubTab = (typeof SUB_TABS)[number]['key']

function ToppSelgereTab() {
  const { t } = useTranslation('bruktbil')
  const { from, to, klasse, varebil, omregistrering, selgerPostnummer, kjoperPostnummer } = useBruktbilFilters()

  const [data, setData] = useState<SelgereResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  // #526 — the control binds to the draft; the query reads the applied value,
  // which only changes when the user clicks Oppdater in the filter box.
  const [classificationDraft, setClassificationDraft, classification] =
    useLocalFilter<string | null>('classification', null)

  useEffect(() => { setPage(1) }, [from, to, JSON.stringify(klasse), varebil, omregistrering, selgerPostnummer, kjoperPostnummer, classification])

  useLatest(alive => {
    setLoading(true)
    fetchSelgere({ from, to, klasse, varebil, omregistrering, selgerPostnummer, kjoperPostnummer, classification, groupBy: 'org', page })
      .then(res => { if (alive()) setData(res.data) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [from, to, JSON.stringify(klasse), varebil, omregistrering, selgerPostnummer, kjoperPostnummer, classification, page])

  return (
    <>
      <FilterSlot>
        <ClassificationFilter
          value={classificationDraft}
          onChange={setClassificationDraft}
          label={t('orgList.selgersType')}
        />
      </FilterSlot>

      {loading && (
        <div className="card p-4 animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />
      )}
      {!loading && data && (
        <SelgereTable
          rows={data.data}
          page={data.page}
          pages={data.pages}
          total={data.total}
          onPageChange={setPage}
        />
      )}
    </>
  )
}

export default function SelgerePage() {
  const { t } = useTranslation('bruktbil')
  const { from, to, periodExplicit, varebil, tab, setFilters } = useBruktbilFilters()
  const maxDataDate = useMaxDataDate()
  const filterLines = useExplanationFilterLines({ orgTypeLabel: t('orgList.selgersType') })
  const active: SubTab = (SUB_TABS.some(st => st.key === tab) ? tab : 'topp') as SubTab

  const vehicleFilter = varebil === false ? 'personbil' : varebil === true ? 'varebil' : 'alle'

  return (
    <div className="space-y-3">
      <QueryExplanationBox
        maxDataDate={maxDataDate}
        // An explicitly chosen period is already exactly what the user asked
        // for, so show it verbatim; only the implicit default runs to today,
        // past where data goes, and needs clipping to the cutoff (#594 AC4).
        outputMode={periodExplicit ? 'custom' : 'year_to_date'}
        vehicleFilter={vehicleFilter as 'personbil' | 'varebil' | 'alle'}
        startDate={from}
        endDate={to}
        periodLabel={t('oversikt.valgtPeriode')}
        filterLines={filterLines}
      />

      <SubTabBar
        parent={t('layout.tabs.selgere')}
        tabs={SUB_TABS.map(st => ({ ...st, label: t(st.labelKey) }))}
        active={active}
        onChange={key => setFilters({ tab: key })}
        testIdPrefix="selgere-tab-"
      />

      {active === 'topp' && <ToppSelgereTab />}
      {active === 'sammenligning' && <OrgSammenligningTab side="selger" />}
    </div>
  )
}
